import { useSyncExternalStore } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { formatRelativeTime, type RelativeTime } from './format-relative-time';

const TICK_MS = 30_000;

const listeners = new Set<() => void>();
let tick = 0;
let intervalId: ReturnType<typeof setInterval> | null = null;

function startInterval() {
  if (intervalId !== null) return;
  intervalId = setInterval(() => {
    tick += 1;
    listeners.forEach((listener) => listener());
  }, TICK_MS);
}

function stopInterval() {
  if (intervalId === null) return;
  clearInterval(intervalId);
  intervalId = null;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  startInterval();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) stopInterval();
  };
}

function getSnapshot() {
  return tick;
}

function getServerSnapshot() {
  return 0;
}

const subscribeMounted = () => () => undefined;
const getMountedSnapshot = () => true;
const getMountedServerSnapshot = () => false;

// Relative time depends on the current clock, which differs between the SSR
// server (UTC, render time) and the browser (local TZ, hydration time). Gate the
// live value behind a mount flag so the server and first client paint both return
// null — callers render a deterministic fallback then — and the relative value
// only appears after hydration, avoiding React #418 mismatches.
export function useRelativeTime(value: string | null): RelativeTime | null {
  const mounted = useSyncExternalStore(
    subscribeMounted,
    getMountedSnapshot,
    getMountedServerSnapshot,
  );
  useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  if (!value || !mounted) return null;
  return formatRelativeTime(new Date(value));
}

export function formatRelativeTimeLabel(rel: RelativeTime, t: TFunction): string {
  if (rel.unit === 'now') return t('songs.relativeTime.NOW');
  if (rel.unit === 'second' || rel.unit === 'minute' || rel.unit === 'hour') {
    return t(`songs.relativeTime.${rel.unit}`, { n: rel.n });
  }
  return t(`songs.relativeTime.${rel.unit}`, { count: rel.n });
}

/** Localised relative label, or null until hydration settles. */
export function useRelativeTimeLabel(value: string | null): string | null {
  const { t } = useTranslation('common');
  const rel = useRelativeTime(value);
  return rel ? formatRelativeTimeLabel(rel, t) : null;
}
